<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { VisuallyHidden } from 'radix-vue'
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from '@/components/ui/drawer'
import Badge from '@/components/ui/badge/Badge.vue'
import RankIcon from '@/assets/icons/rank.svg'
import RankProgress from '@/components/ui/RankProgress.vue'
import ScoreWidget from '@/components/score/ScoreWidgetMobile.vue'
import Actions from '@/components/actions/Actions.vue'
import ActionsShort from '@/components/actions/ActionsShort.vue'
import { Button } from '@/components/ui/button'
import WrongRecommendation from '@/assets/wrong-recommendation.svg'
import SmallPlayingCard from '../../views/SmallPlayingCard.vue'
import type { Move } from '@/types'
import RankBenefits from '@/components/RankBenefits.vue'
import Typography from '@/components/ui/typography/Typography.vue'
import CloseIcon from '@/assets/X.svg'
import { storeToRefs } from 'pinia'
import { useAuth } from '@/stores/auth'
import { getPlayer } from '@/api/getPlayer'
import { unmountChatUIWebComponent } from '@/helpers/integrations'
import type { PlayerStatistic } from '@acepoker/contract'

const { moves, statisticInfo } = defineProps<{
  moves?: Move[]
  statisticInfo?: PlayerStatistic
}>()

const emit = defineEmits<{
  cancel: []
  endRound: []
}>()

const LEVEL_SIZE = 1000

const { player } = storeToRefs(useAuth())
const username = 'Max moneymaker'
const rank = computed(() => Math.floor(player.value ? player.value.experience / LEVEL_SIZE : 0))
const progress = ref({
  start: 0,
  current: 0,
  previous: 0,
  next: 1
})
const progressPercent = computed(() =>
  Math.min(
    ((progress.value.current - progress.value.start) /
      (progress.value.next - progress.value.start)) *
      100,
    100
  )
)
const progressDelta = computed(() => progress.value.current - progress.value.previous)

onMounted(async () => {
  const updatedPlayer = await getPlayer()
  const playerExperience = player.value ? player.value.experience : 0
  const bottomLimit = Math.floor(playerExperience / LEVEL_SIZE) * LEVEL_SIZE
  progress.value = {
    start: bottomLimit,
    current: updatedPlayer.experience,
    previous: playerExperience,
    next: bottomLimit + LEVEL_SIZE
  }
  player.value = updatedPlayer
})

const isSessionAnalysis = ref(false)
const drawerHeight = ref(75)

const onSessionAnalysis = () => {
  isSessionAnalysis.value = true
  drawerHeight.value = 100
}

const onExitClick = () => {
  unmountChatUIWebComponent()
  emit('endRound')
}

const onClose = () => {
  isSessionAnalysis.value = false
  drawerHeight.value = 75
}

onUnmounted(() => {
  unmountChatUIWebComponent()
})
</script>

<template>
  <drawer @update:open="(value) => !value && $emit('cancel')">
    <drawer-content>
      <visually-hidden><drawer-title>End round</drawer-title></visually-hidden>
      <visually-hidden><drawer-description>Session statistics</drawer-description></visually-hidden>

      <div
        class="bg-background flex w-full flex-col items-center px-4 py-10 text-white"
        :style="{
          'max-height': drawerHeight + 'dvh'
        }"
      >
        <div v-if="!isSessionAnalysis" class="absolute -top-10 right-4">
          <close-icon @click="$emit('cancel')" class="h-7 w-7" />
        </div>
        <div v-if="isSessionAnalysis" class="flex w-full justify-end">
          <close-icon @click="onClose" class="h-7 w-7" />
        </div>
        <img
          src="@/assets/icons/statistic/avatar.png"
          srcset="@/assets/icons/statistic/avatar_x2.png 2x"
          alt="logo"
          class="h-[54px] w-[54px] rounded-full"
        />
        <Typography size="text-16-24" color="secondary" class="mb-[8px]">{{ username }}</Typography>
        <Badge
          class="bg-highlight pointer-events-none mb-[20px] flex h-[34px] min-w-[100px] justify-between rounded text-lg font-semibold text-nowrap"
        >
          <RankIcon class="mr-[3px] h-[18px] w-[11px]" />
          {{ $t('Statistic.label.Rank') }}
          {{ rank }}
        </Badge>
        <rank-progress
          v-model="progressPercent"
          class="mb-2.5"
          :delta="progressDelta"
          :current="progress.current"
        />
        <div class="mb-6 flex w-full flex-row justify-between">
          <Typography color="secondary">{{ progress.start }}</Typography>
          <Typography color="inactive">{{ $t('Statistic.label.toNextRank') }}</Typography>
          <Typography color="secondary">{{ progress.next }}</Typography>
        </div>

        <div class="flex h-full w-full flex-col items-center overflow-y-auto" data-vaul-no-drag>
          <actions-short v-if="moves?.length" :items="moves" class="mb-6" />
          <template v-if="false">
            <!-- temporary hidden -->
            <Typography class="mb-[6px] w-full text-base font-semibold">{{
              $t('Statistic.label.Recommendation')
            }}</Typography>
            <div class="mb-6 min-h-[126px] w-full overflow-x-auto overflow-y-hidden">
              <div class="flex w-full gap-2">
                <div
                  class="bg-dark-grey flex min-w-[232px] flex-col gap-[6px] rounded p-[14px]"
                  v-for="index in 3"
                  :key="index"
                >
                  <div class="flex items-center gap-2">
                    <div class="flex gap-1">
                      <small-playing-card value="As" class="min-h-[28px] min-w-5" />
                      <small-playing-card value="Ks" class="min-h-[28px] min-w-5" />
                    </div>
                    <div class="flex gap-1">
                      <small-playing-card value="Ts" class="min-h-[28px] min-w-5" />
                      <small-playing-card value="9s" class="min-h-[28px] min-w-5" />
                      <small-playing-card value="8s" class="min-h-[28px] min-w-5" />
                    </div>
                    <div>+</div>
                    <small-playing-card value="7s" class="min-h-[28px] min-w-5" />
                  </div>
                  <div class="flex gap-2">
                    <wrong-recommendation class="h-3 min-w-3" />
                    <div class="text-text-helper text-xs font-normal">
                      {{ $t('Statistic.label.MoveResult') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-if="isSessionAnalysis">
            <ScoreWidget v-if="statisticInfo" :statistic="statisticInfo" class="mb-6" />
            <RankBenefits />
            <actions v-if="moves" :items="moves" class="mb-6" />
            <div class="mb-[7px] w-full text-base font-semibold text-white">
              {{ $t('Statistic.label.Recommendation') }}
            </div>
            <div class="text-secondary mb-3 w-full text-sm font-normal">
              {{ $t('EndSession.label.ImproveSkills') }}
            </div>
            <div
              class="bg-dark-grey mb-1 flex w-full flex-col rounded-sm px-[14px] py-3"
              v-for="index in 3"
              :key="index"
            >
              <div class="text-17 mb-[3.5px] font-medium text-white">
                {{ $t('EndSession.label.PokerTitle') }}
              </div>
              <div class="mb-[7px] flex items-center gap-[6px]">
                <div class="text-secondary text-xs font-normal">
                  {{ $t('EndSession.label.Straddle') }}
                </div>
                <div class="h-4 w-px bg-[rgba(255,255,255,0.6)]" />
                <div class="text-secondary text-xs font-normal">8 {{ $t('label.max') }}</div>
                <div class="h-4 w-px bg-[rgba(255,255,255,0.6)]" />
                <div class="text-secondary text-xs font-normal">
                  {{ $t('EndSession.label.Blind') }}
                </div>
              </div>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-1">
                  <div class="bg-menu-item-bg flex items-center gap-[2px] rounded px-1 py-px">
                    <div class="text-secondary text-sm font-normal">
                      {{ $t('EndSession.label.Hands') }}
                    </div>
                    <div class="text-sm font-semibold text-white">253</div>
                  </div>
                  <div class="bg-menu-item-bg flex items-center gap-[2px] rounded px-1 py-px">
                    <div class="text-secondary text-sm font-normal">
                      {{ $t('EndSession.label.Score') }}
                    </div>
                    <div class="text-sm font-semibold text-white">648</div>
                  </div>
                </div>
                <div class="bg-success flex items-center gap-[2px] rounded px-1 py-px">
                  <div class="text-sm font-normal text-white">
                    {{ $t('EndSession.label.Qualities') }}
                  </div>
                  <div class="text-sm font-semibold text-white">86%</div>
                </div>
              </div>
            </div>
            <div class="mb-5" />
          </template>
          <div class="flex w-full flex-col gap-[10px]">
            <slot name="button-primary" />
            <div class="flex items-center gap-[10px]">
              <div
                v-if="$slots['button-secondary']"
                :class="{
                  'w-1/2': !isSessionAnalysis,
                  'w-full': isSessionAnalysis
                }"
              >
                <slot name="button-secondary"></slot>
              </div>
              <Button
                v-if="!isSessionAnalysis"
                :class="{
                  'w-1/2': $slots['button-secondary'],
                  'w-full': !$slots['button-secondary']
                }"
                @click="onSessionAnalysis"
                variant="ghostOutlined"
              >
                {{ $t('EndSession.button.Analysis') }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </drawer-content>
  </drawer>
</template>
